'use client';

import { Grid2, Box, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useResponsiveBreakpoints } from '@/hooks/useResponsiveBreakpoints';
import { useComputedColor } from '@/hooks/useComputedColor';
import type { CampaignConfig } from '../config.interface';
import { Text } from '@/ui/Text';
import { useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import { SeeCampaigns } from './SeeCampaigns';
import { ResumeShortImageContainer } from './ResumeShortImageContainer';
import { CampaignCollectedCounter } from './CampaignCollectedCounter';
import { CampaignResumeShortDonate } from './CampaignResumeShortDonate';
import { SeeCampaignInfo } from '@/modules/donate/SeeCampaignInfo';
import type { AppConfig } from '../AppConfig';
import { ActitudSolidariaPresentation } from '@/custom/actitudsolidaria/actitudsolidaria-presentation';

export const CampaignResumeShort = ({
	isCampaignInfoPage,
	showDonateButton,
	config,
	appConfig,
}: {
	isCampaignInfoPage: boolean;
	showDonateButton?: boolean;
	config: CampaignConfig;
	appConfig: AppConfig;
}) => {
	const { extraSmall, small, large, medium } = useResponsiveBreakpoints();
	const campaignImage = useCorporateStore((state) => state.metadata.media?.location ?? '');

	const textColor = useComputedColor();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

	const alignItems = extraSmall || small || medium || large ? 'flex-start' : 'center';
	const flexDirection = extraSmall || small || medium || large ? 'column' : 'row';

	const percentageCollected = useMemo(
		() =>
			(Math.round(config.aggregate.collected) /
				Math.round(config.campaign.budget.funding ?? config.aggregate.collected)) *
			100,
		[config.campaign, config.aggregate.collected],
	);

	const sxseeCampaignInfo = {
		display: 'none',
		background: 'black',
		[theme.breakpoints.down('xs')]: {
			display: 'block',
		},
		[theme.breakpoints.between('sm', 'sm')]: {
			display: 'block',
		},
	};

	const sxshortDescription = {
		'lineHeight': '24px',
		'textAlign': 'left',
		'& p': {
			marginTop: 0,
		},
		'& p:not(:first-child)': {
			marginTop: '10px',
		},
		// [theme.breakpoints.up('md')]: {
		// 	paddingTop: '2rem',
		// },
	};

	const sxcampaignDonateButtonUp = {
		display: 'flex',
		flexDirection: 'column-reverse',
	};

	return (
		<Grid2 size={{ xs: 12 }}>
			<Grid2 container direction="row" justifyContent="flex-start">
				<Grid2
					size={{ xs: 12 }}
					style={{ textAlign: 'center', padding: '0.44rem 0', backgroundColor: 'white' }}
					sx={sxseeCampaignInfo}
				>
					{isCampaignInfoPage ? <SeeCampaigns /> : <SeeCampaignInfo campaignId={config.campaign.id} />}
				</Grid2>
				{config.actitudSolidariaPresentation ? (
					<ActitudSolidariaPresentation width="100%" />
				) : (
					<ResumeShortImageContainer
						campaignId={config.campaign.id}
						campaignImage={campaignImage}
						title={config.campaign.texts?.corporateTitle ?? ''}
						config={config}
					/>
				)}
				<Box
					sx={config.campaignDonateButtonUp && isMobile ? sxcampaignDonateButtonUp : undefined}
					display={config.campaignDonateButtonUp ? undefined : 'flex'}
					width="100%"
					pt={4}
					pb={15}
					mb={medium ? 4 : 0}
					flexDirection={config.campaignDonateButtonUp && isMobile ? undefined : flexDirection}
					paddingTop={config.showAlalaWebm && isTablet && !isDesktop ? '2rem' : undefined}
				>
					<Box
						width={config.disableCounters ? '100%' : 'auto'}
						flex={1}
						pl={extraSmall ? 2 : small ? 5 : medium ? 3 : large ? 4 : 5}
						pr={extraSmall ? 2 : small ? 5 : medium ? 3 : large ? 4 : 5}
						mb={extraSmall ? 4 : 0}
					>
						<Text text={config.campaign.texts?.resumeShort} sx={sxshortDescription} style={{ color: textColor }} />
					</Box>
					{!config.disableCounters && (
						<Box
							flex={1}
							pl={extraSmall ? 2 : small ? 5 : medium ? 3 : large ? 4 : 5}
							pr={extraSmall ? 2 : small ? 5 : medium ? 3 : large ? 4 : 5}
							mt={extraSmall ? 3 : 0}
							sx={config.campaignDonateButtonUp && isMobile ? sxcampaignDonateButtonUp : undefined}
							gap={config.campaignDonateButtonUp && isMobile ? '2.5rem' : undefined}
						>
							<Box
								display="flex"
								alignItems={alignItems}
								justifyContent="space-between"
								paddingTop={config.hideCollectedCounter ? '1.5rem' : undefined}
							>
								<CampaignCollectedCounter
									collected={config.aggregate.collected}
									donors={
										appConfig.customDonors?.(config.aggregate.donors, config.campaign.id) ?? config.aggregate.donors
									}
									percentageCollected={percentageCollected}
									config={config}
								/>
							</Box>
							<CampaignResumeShortDonate
								finished={config.campaign.finished}
								campaignId={config.campaign.id}
								showDonateButton={showDonateButton ?? config.showDonateButton}
								campaignTexts={config.campaign.texts}
								config={config}
							/>
						</Box>
					)}
				</Box>
			</Grid2>
		</Grid2>
	);
};
